import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router';
import { Layout } from './components/Layout';
import { Spinner } from './components/ui';
import { AuthProvider, useAuth } from './lib/auth';
import { AppErrorBoundary, installStaleCodeReload } from './lib/version';
import { CandidatePage } from './pages/Candidate';
import { AdminCompanyPage } from './pages/admin/Company';
import { AdminCompaniesPage, AdminOverviewPage } from './pages/admin/Overview';
import { AdminSystemPage } from './pages/admin/System';
import { JobEditorPage } from './pages/JobEditor';
import { NewJobPage } from './pages/NewJob';
import { JobPipelinePage } from './pages/JobPipeline';
import { JobsPage } from './pages/Jobs';
import { LivePage } from './pages/Live';
import { AnalyticsPage } from './pages/Analytics';
import { ApplyPage } from './pages/Apply';
import { ReportPage } from './pages/Report';
import { OverviewPage } from './pages/Overview';
import { LoginPage } from './pages/Login';
import { SchedulerPage } from './pages/Scheduler';
import { SettingsPage } from './pages/Settings';
import { InterviewRoom } from './room/InterviewRoom';
import { Schedule } from './room/Schedule';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 5_000 } },
});

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-muted-fg">
        <Spinner />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return <>{children}</>;
}

installStaleCodeReload();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppErrorBoundary>
          <Routes>
            {/* Candidate-facing: no recruiter session involved. */}
            <Route path="/i/:token" element={<InterviewRoom />} />
            <Route path="/i/:token/schedule" element={<Schedule />} />
            <Route path="/apply/:job/:key" element={<ApplyPage />} />
            {/* A hiring manager's read-only report, shared by a recruiter. */}
            <Route path="/r/:token" element={<ReportPage />} />
            <Route
              path="/*"
              element={
                <AuthProvider>
                  <Routes>
                    <Route path="/login" element={<LoginPage />} />
                    <Route
                      element={
                        <RequireAuth>
                          <Layout />
                        </RequireAuth>
                      }
                    >
                      <Route index element={<OverviewPage />} />
                      <Route path="jobs" element={<JobsPage />} />
                      <Route path="jobs/new" element={<NewJobPage />} />
                      <Route path="jobs/new/review" element={<JobEditorPage />} />
                      <Route path="jobs/:jobId" element={<JobPipelinePage />} />
                      <Route path="jobs/:jobId/edit" element={<JobEditorPage />} />
                      <Route path="candidates/:candidateId" element={<CandidatePage />} />
                      <Route path="live" element={<LivePage />} />
                      <Route path="analytics" element={<AnalyticsPage />} />
                      <Route path="settings" element={<SettingsPage />} />
                      <Route path="scheduler" element={<SchedulerPage />} />
                      {/* The platform console: platform admins only (each page sends others home). */}
                      <Route path="admin" element={<AdminOverviewPage />} />
                      <Route path="admin/companies" element={<AdminCompaniesPage />} />
                      <Route path="admin/companies/:id" element={<AdminCompanyPage />} />
                      <Route path="admin/system" element={<AdminSystemPage />} />
                      <Route path="companies" element={<Navigate to="/admin/companies" replace />} />
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Route>
                  </Routes>
                </AuthProvider>
              }
            />
          </Routes>
        </AppErrorBoundary>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
